import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Activity,
  Bell,
  Brain,
  CheckCheck,
  CreditCard,
  LifeBuoy,
  PhoneMissed,
  Radar,
  Send,
  Voicemail,
  type LucideIcon,
} from "lucide-react";
import { useT } from "../../i18n/I18nProvider";
import { markCallsSeen } from "../../lib/callSeen";
import { markBrainSeen } from "../../lib/siteBrain";
import { stepPath } from "../sections/prospecting/steps";
import { fmtMoney } from "../ui/format";
import { Pop, useClosePops } from "./Pop";
import { useSignals } from "./SignalsProvider";
import { clearSignals, readSeen, unseenSignals } from "./seenSignals";
import { useDesktopBadge } from "./useDesktopBadge";
import type { SignalRow } from "./signals";

const ICON: Record<SignalRow["kind"], LucideIcon> = {
  overdue: CreditCard,
  brainDoneMine: Brain,
  brainDone: Brain,
  campaign: Send,
  leads: Radar,
  tickets: LifeBuoy,
  callsMissed: PhoneMissed,
  voicemail: Voicemail,
  sitesDown: Activity,
};

const ROUTE: Record<SignalRow["kind"], string> = {
  overdue: "/sales/invoices",
  // The list, not one company: this table is keyed by kind, so a row here can
  // only ever point at a fixed page. The page sorts by newest activity, so what
  // just finished is the first thing on it.
  brainDoneMine: stepPath("brain"),
  brainDone: stepPath("brain"),
  campaign: "/marketing/campaigns",
  // The kind keeps its stored name ("leads", the scanner's new leads); only the
  // page it opens moved, to the call queue tab of Kliendiotsing.
  leads: stepPath("leads"),
  tickets: "/support",
  callsMissed: "/calls?folder=missed",
  voicemail: "/calls?folder=voicemail",
  // The desk rather than one site: more than one can be down at once, and the
  // desk lists the down ones first.
  sitesDown: "/monitoring",
};

/**
 * Which server-held cursor a tracked row belongs to.
 *
 * Partial on purpose: most rows are conditions with no cursor at all, and only
 * the rows marked `tracked` are addressed here.
 */
const MARK_SEEN: Partial<Record<SignalRow["kind"], () => Promise<unknown>>> = {
  callsMissed: markCallsSeen,
  voicemail: markCallsSeen,
  // Both brain rows, one cursor. `clearAll` keys its Set on the FUNCTION, so
  // the two of them ask the server once between them - the same trick that
  // stops the two call streams double-clearing their shared bookmark.
  brainDoneMine: markBrainSeen,
  brainDone: markBrainSeen,
};

/**
 * The bell.
 *
 * Two kinds of line hang here, and the difference decides where each one's
 * "already looked at" lives.
 *
 * Most rows are CONDITIONS that resolve themselves - an overdue invoice, an open
 * ticket, a campaign mid-send. Storing those would mean a second source of truth
 * that goes stale the moment an invoice is paid in another tab, so each is
 * derived from the same live counters the badges use (`signals.ts`) and cannot
 * claim something that is no longer true. What has been looked at is a note in
 * this browser about the count each row had when it was cleared
 * (`seenSignals.ts`), and a row comes straight back the moment its count grows.
 *
 * Missed calls and voicemail are EVENTS. A call missed at 14:32 stays missed,
 * and nothing in the product resolves it, so the honest per-operator question is
 * "anything since I last looked" - one timestamp, held by the server and shared
 * across every machine that operator signs in on. Those rows arrive already
 * filtered (`tracked: true`); the local store does not touch them.
 *
 * Which is why the footer does two things at once and is still labelled for what
 * it does rather than "mark all read": it hides the derived rows in this browser
 * and moves the call cursor on the server. Both are "stop showing me these"; only
 * one of them is a claim about having read anything.
 */
export function AlertsMenu({ userId }: { userId: string }) {
  const t = useT();
  const navigate = useNavigate();
  const close = useClosePops();
  const { signals, overdueCents, refresh } = useSignals();
  const [seen, setSeen] = useState(() => readSeen(userId));

  // The pip counts what is shown, not what exists: a bell claiming four over an
  // empty list is the fastest way to teach somebody to ignore the bell.
  const rows = unseenSignals(signals, seen);

  // The macOS app carries the same number on its Dock icon, and says out loud
  // when one of these rows grows. Inert in a browser.
  useDesktopBadge(rows, (row) => t(`alerts.${row.kind}`, { n: row.count }));

  const go = (to: string) => {
    close();
    navigate(to);
  };

  const detailFor = (row: SignalRow) => {
    if (row.kind === "overdue") return fmtMoney(overdueCents);
    return row.detail;
  };

  const clearAll = () => {
    setSeen(clearSignals(rows, userId));
    // A failed mark-read leaves the rows standing, which is the right way round:
    // the alternative is hiding a voicemail on the strength of a request that
    // never landed.
    //
    // A Set of the functions themselves, so the two call streams - which share
    // one cursor endpoint - ask once rather than twice. A tracked kind missing
    // from `MARK_SEEN` would be hidden in this browser and come straight back on
    // the next poll, since the local store does not filter tracked rows.
    const marks = new Set(rows.filter((row) => row.tracked).map((row) => MARK_SEEN[row.kind]));
    marks.delete(undefined);
    if (marks.size > 0) {
      void Promise.all([...marks].map((mark) => mark!()))
        .then(refresh)
        .catch(() => {});
    }
    close();
  };

  return (
    <Pop
      wide
      trigger={(open) => (
        <button
          type="button"
          className="crm-rb"
          title={t("alerts.title")}
          aria-label={t("alerts.title")}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <Bell className="size-[18px]" strokeWidth={1.7} />
          {rows.length ? <span className="crm-pip">{rows.length}</span> : null}
        </button>
      )}
    >
      <div className="crm-menu-head">
        <div className="crm-menu-t">{t("alerts.title")}</div>
        <div className="crm-menu-s">{t("alerts.sub", { n: rows.length })}</div>
      </div>

      {rows.length === 0 ? (
        <div className="crm-pal-empty">{t("alerts.empty")}</div>
      ) : (
        <>
          {rows.map((row) => {
            const Icon = ICON[row.kind];
            const detail = detailFor(row);
            return (
              <button key={row.key} type="button" className="crm-mi" onClick={() => go(ROUTE[row.kind])}>
                <Icon className="size-4" />
                <span className="min-w-0 flex-1">
                  <span className="crm-trunc block">{t(`alerts.${row.kind}`, { n: row.count })}</span>
                  {detail ? <span className="crm-mi-sub crm-trunc block">{detail}</span> : null}
                </span>
              </button>
            );
          })}
          <div className="crm-menu-sep" />
          <div className="crm-menu-foot">
            <button type="button" className="crm-mi" title={t("alerts.clearHint")} onClick={clearAll}>
              <CheckCheck className="size-4" />
              {t("alerts.clear")}
            </button>
          </div>
        </>
      )}
    </Pop>
  );
}
